(function () {
  const STORAGE_KEY = 'pp-archive-settings-v1';
  const PAGE_SIZE = 10;

  function parseDataJSON(script) {
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('Unable to parse archive JSON data.', error);
      return { articles: [] };
    }
  }

  function parseTranslationsJSON(script) {
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('Unable to parse translations JSON data.', error);
      return {
        "on": "ON",
        "off": "OFF",
        "showing_articles": "Showing {0} of {1} articles",
        "no_posts": "No posts yet",
        "pagination_previous": "Previous",
        "pagination_next": "Next"
      };
    }
  }

  function formatPositional(template, ...args) {
    return template.replace(/{(\d+)}/g, (match, index) => {
      return typeof args[index] !== 'undefined' ? args[index] : match;
    });
  }

  function formatDate(dateString, locale = "nl-NL") {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).toUpperCase();
  }

  function loadSettings() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch {
      return {};
    }
  }

  function saveSettings(settings) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }

  function filterArticles(articles, cutoff) {
    if (cutoff === 'all') return articles;
    const now = new Date();
    const months = cutoff === '12m' ? 12 : cutoff === '6m' ? 6 : cutoff === '3m' ? 3 : 0;
    if (!months) return articles;
    return articles.filter((article) => {
      const published = new Date(article.publishedAt);
      if (Number.isNaN(published.getTime())) return false;
      const cutoffDate = new Date(now);
      cutoffDate.setMonth(cutoffDate.getMonth() - months);
      return published >= cutoffDate;
    });
  }

  function sortArticles(articles, sortOrder) {
    return articles.slice().sort((a, b) => {
      const left = new Date(a.publishedAt).getTime();
      const right = new Date(b.publishedAt).getTime();
      if (Number.isNaN(left) || Number.isNaN(right)) return 0;
      return sortOrder === 'oldest' ? left - right : right - left;
    });
  }

  function buildArticleNode(article, index, viewMode, listItemTemplate) {
    const template = listItemTemplate;
    if (!template) return document.createElement('li');

    const node = document.importNode(template.content, true);
    const item = node.querySelector('li');
    const link = node.querySelector('.link');
    const imageLink = node.querySelector('.link-image');
    const image = node.querySelector('.link-image img');
    const author = node.querySelector('.author');
    const date = node.querySelector('.date');
    const title = article.title || '';
    const url = article.url || '#';
    const publishedDate = formatDate(article.publishedAt, article.inLanguage);
    const authorName = article.author || '';
    const imageUrl = article.image || '';

    if (link) {
      link.textContent = title;
      link.setAttribute('href', url);
    }
    if (imageLink && image) {
      if (imageUrl) {
        imageLink.hidden = false;
        imageLink.setAttribute('href', url);
        image.setAttribute('src', imageUrl);
        image.setAttribute('alt', title);
        const imageBase = article.imageBase || '';
        const intrinsicWidth = Number(article.imageWidth) || 0;
        const intrinsicHeight = Number(article.imageHeight) || 0;
        if (imageBase) {
          const widths = [400, 600, 800, 1200, 1600, 2000, 2400, 2800, 3200].filter((width) => !intrinsicWidth || width <= intrinsicWidth);
          if (intrinsicWidth && widths.indexOf(intrinsicWidth) === -1) widths.push(intrinsicWidth);
          widths.sort((left, right) => left - right);
          image.setAttribute('srcset', widths.map((width) => `${imageBase}?auto=format&fit=max&q=85&w=${width} ${width}w`).join(', '));
          let coverScale = 1;
          if (intrinsicWidth && intrinsicHeight) {
            coverScale = Math.min(2.5, Math.max(1, intrinsicWidth / intrinsicHeight));
          }
          const cover = Math.round(coverScale * 100) / 100;
          image.setAttribute('sizes', `(max-width: 860px) calc((100vw - 40px) * ${cover}), ${Math.round(400 * cover)}px`);
        }
      } else {
        imageLink.hidden = true;
        image.removeAttribute('src');
        image.setAttribute('alt', '');
      }
    }
    if (author) author.textContent = authorName;
    if (date) date.textContent = publishedDate;

    return item || node;
  }

  class BlogArchive extends HTMLElement {
    constructor() {
      super();
      this.settings = Object.assign({ sort: 'newest', view: 'grid', cutoff: 'all' }, loadSettings());
      this.articles = [];
      this.filteredArticles = [];
      this.currentPage = 1;
      this.totalPages = 1;
      this.pageParamName = 'archive-page';
      this._initialized = false;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.dataScript = this.querySelector('[data-archive-content]');
      this.translationsScript = this.querySelector('[data-archive-translations]');
      this.list = this.querySelector('[data-archive-list]');
      this.status = this.querySelector('[data-archive-status]');
      this.sortButtons = Array.from(this.querySelectorAll('[data-action="sort"]'));
      this.viewButtons = Array.from(this.querySelectorAll('[data-action="view"]'));
      this.cutoffSelect = this.querySelector('[data-action="cutoff"]');
      this.listItemTemplate = this.querySelector('template[data-template="list-item"]');
      this.pagination = this.querySelector('[data-archive-pagination]');
      this.paginationPrev = this.querySelector('[data-archive-page-prev]');
      this.paginationNext = this.querySelector('[data-archive-page-next]');
      this.paginationPages = this.querySelector('[data-archive-page-list]');
      this.articles = parseDataJSON(this.dataScript).articles || [];
      this.translations = parseTranslationsJSON(this.translationsScript);

      // empty panel element: allow markup in DOM via [data-archive-empty],
      // otherwise create a simple fallback node.
      this.emptyPanel = this.querySelector('[data-archive-empty]');
      if (!this.emptyPanel) {
        this.emptyPanel = document.createElement('div');
        this.emptyPanel.setAttribute('data-archive-empty', '');
        this.emptyPanel.className = 'archive-empty';
        this.emptyPanel.hidden = true;
        this.emptyPanel.textContent = this.translations.no_posts || 'No posts yet';
        if (this.list && this.list.parentNode) this.list.parentNode.insertBefore(this.emptyPanel, this.list.nextSibling);
      }

      this.init();
    }

    init() {
      this.currentPage = this.getPageFromUrl();
      this.cutoffSelect.value = this.settings.cutoff;
      this.updateActiveButtons();
      this.updateViewModeClass();
      this.bindEvents();
      this.applyFilters();
      this.renderPage(true);
      this.updatePagination();
    }

    bindEvents() {
      this.sortButtons.forEach((button) => {
        button.addEventListener('click', () => this.onSortChange(button.dataset.value));
      });
      this.viewButtons.forEach((button) => {
        button.addEventListener('click', () => this.onViewChange(button.dataset.value));
      });
      this.cutoffSelect.addEventListener('change', () => this.onCutoffChange(this.cutoffSelect.value));
      if (this.paginationPrev) {
        this.paginationPrev.addEventListener('click', () => this.goToPage(this.currentPage - 1));
      }
      if (this.paginationNext) {
        this.paginationNext.addEventListener('click', () => this.goToPage(this.currentPage + 1));
      }
      if (this.paginationPages) {
        this.paginationPages.addEventListener('click', (event) => {
          const pageButton = event.target.closest('button[data-page]');
          if (pageButton) {
            this.goToPage(Number(pageButton.dataset.page));
          }
        });
      }
    }

    onSortChange(value) {
      if (!value || this.settings.sort === value) return;
      this.settings.sort = value;
      this.saveAndRefresh();
      this.updateActiveButtons();
    }

    onViewChange(value) {
      if (!value || this.settings.view === value) return;
      this.settings.view = value;
      this.updateActiveButtons();
      this.updateViewModeClass();
      this.renderPage(true);
      this.updatePagination();
      saveSettings(this.settings);
    }

    onCutoffChange(value) {
      this.settings.cutoff = value;
      this.saveAndRefresh();
      this.updateActiveButtons();
    }

    saveAndRefresh() {
      saveSettings(this.settings);
      this.applyFilters();
      this.goToPage(1, false);
    }

    updateActiveButtons() {
      this.sortButtons.forEach((button) => {
        button.classList.toggle('on', button.dataset.value === this.settings.sort);
      });
      this.viewButtons.forEach((button) => {
        button.classList.toggle('on', button.dataset.value === this.settings.view);
      });
    }

    updateViewModeClass() {
      this.classList.toggle('view-grid', this.settings.view === 'grid');
      this.classList.toggle('view-list', this.settings.view === 'list');
    }

    applyFilters() {
      this.filteredArticles = sortArticles(filterArticles(this.articles, this.settings.cutoff), this.settings.sort);
      this.totalPages = Math.max(1, Math.ceil(this.filteredArticles.length / PAGE_SIZE));
      this.currentPage = Math.min(this.currentPage, this.totalPages);
    }

    getPageFromUrl() {
      if (!window.location) return 1;
      const params = new URLSearchParams(window.location.search);
      const page = Number(params.get(this.pageParamName));
      return Number.isInteger(page) && page > 0 ? page : 1;
    }

    updateUrlPage() {
      const url = new URL(window.location.href);
      if (this.currentPage > 1) {
        url.searchParams.set(this.pageParamName, String(this.currentPage));
      } else {
        url.searchParams.delete(this.pageParamName);
      }
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }

    goToPage(page, syncUrl = true) {
      const totalPages = Math.max(1, Math.ceil(this.filteredArticles.length / PAGE_SIZE));
      const safePage = Math.min(Math.max(1, Number(page) || 1), totalPages);
      if (this.currentPage === safePage) {
        this.renderPage(true);
        this.updatePagination();
        return;
      }
      this.currentPage = safePage;
      if (syncUrl) this.updateUrlPage();
      this.renderPage(true);
      this.updatePagination();
    }

    renderPage(reset = false) {
      if (reset) {
        this.list.innerHTML = '';
      }

      const start = (this.currentPage - 1) * PAGE_SIZE;
      const end = start + PAGE_SIZE;
      const slice = this.filteredArticles.slice(start, end);
      const viewMode = this.settings.view;
      const baseIndex = start;
      const fragment = document.createDocumentFragment();

      slice.forEach((article, idx) => {
        fragment.appendChild(buildArticleNode(article, baseIndex + idx, viewMode, this.listItemTemplate));
      });

      this.list.appendChild(fragment);
      this.updateStatus();
      this.updateEmptyState();
    }

    updateStatus() {
      const count = this.filteredArticles.length;
      const start = (this.currentPage - 1) * PAGE_SIZE;
      const visibleCount = this.filteredArticles.slice(start, start + PAGE_SIZE).length;
      const shown = count ? Math.min(start + visibleCount, count) : 0;
      this.status.textContent = formatPositional(this.translations.showing_articles, shown, count);
    }

    updatePagination() {
      if (!this.pagination || !this.paginationPrev || !this.paginationNext || !this.paginationPages) return;
      this.totalPages = Math.max(1, Math.ceil(this.filteredArticles.length / PAGE_SIZE));
      this.currentPage = Math.min(this.currentPage, this.totalPages);
      if (this.filteredArticles.length <= PAGE_SIZE) {
        this.pagination.hidden = true;
        return;
      }
      this.pagination.hidden = false;
      this.paginationPrev.disabled = this.currentPage <= 1;
      this.paginationNext.disabled = this.currentPage >= this.totalPages;

      const pages = [];
      for (let index = 1; index <= this.totalPages; index += 1) {
        pages.push(index);
      }

      this.paginationPages.innerHTML = '';
      pages.forEach((pageNumber) => {
        const pageButton = document.createElement('button');
        pageButton.type = 'button';
        pageButton.className = `archive-pagination__page${pageNumber === this.currentPage ? ' is-current' : ''}`;
        pageButton.dataset.page = String(pageNumber);
        pageButton.textContent = String(pageNumber);
        this.paginationPages.appendChild(pageButton);
      });

      this.updateUrlPage();
    }

    updateEmptyState() {
      const hasAny = (this.filteredArticles && this.filteredArticles.length > 0);
      if (!hasAny) {
        if (this.list) this.list.hidden = true;
        if (this.pagination) this.pagination.hidden = true;
        if (this.emptyPanel) this.emptyPanel.hidden = false;
      } else {
        if (this.list) this.list.hidden = false;
        if (this.pagination) this.pagination.hidden = false;
        if (this.emptyPanel) this.emptyPanel.hidden = true;
      }
    }
  }

  if (!window.customElements.get('blog-archive')) {
    window.customElements.define('blog-archive', BlogArchive);
  }
})();
